/**
 * Provider Configuration Form dialog component.
 * Provides form for creating and editing LLM provider configurations
 * with conditional fields based on provider type.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { TextField } from '../../@core/components/mui';
import { llmService } from '../../services/llmService';
import { useNotificationStore } from '../../store/notificationStore';
import type { LlmProviderDto, LlmProviderConfigDto } from '../../types/llm';

interface ProviderConfigFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  config: LlmProviderConfigDto | null;
  providers: LlmProviderDto[];
  /**
   * Set of provider IDs that already have a configuration for the current tenant.
   * When adding a new config, those providers are shown as disabled options with
   * a hint, so users understand why a provider (e.g. OpenAI) is unavailable
   * instead of having it silently missing from the dropdown.
   */
  configuredProviderIds?: Set<string>;
}

/**
 * Determines which fields to show based on the provider name.
 */
const getProviderFields = (providerName: string) => {
  const name = providerName.toLowerCase();
  if (name.includes('azure')) {
    return {
      showBaseUrl: true,
      showDeploymentName: true,
      showApiVersion: true,
      showOrganizationId: false,
      showProjectId: true,
      showSecretKey: false,
      baseUrlLabel: 'Azure OpenAI Endpoint',
      baseUrlPlaceholder: 'https://your-resource.openai.azure.com/',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: 'gpt-4o',
      apiVersionPlaceholder: '2024-08-01-preview',
    };
  }
  if (name.includes('amazon') || name.includes('bedrock')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: true,
      baseUrlLabel: 'AWS Region',
      baseUrlPlaceholder: 'e.g. us-east-1',
      apiKeyLabel: 'Access Key ID',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  if (name.includes('openai') && !name.includes('azure')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: false,
      showOrganizationId: true,
      showProjectId: true,
      showSecretKey: false,
      baseUrlLabel: 'Base URL (optional)',
      baseUrlPlaceholder: 'https://api.openai.com/v1',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '',
    };
  }
  if (name.includes('anthropic')) {
    return {
      showBaseUrl: true,
      showDeploymentName: false,
      showApiVersion: true,
      showOrganizationId: false,
      showProjectId: false,
      showSecretKey: false,
      baseUrlLabel: 'Base URL (optional)',
      baseUrlPlaceholder: 'https://api.anthropic.com',
      apiKeyLabel: 'API Key',
      deploymentNamePlaceholder: '',
      apiVersionPlaceholder: '2023-06-01',
    };
  }
  // Default: show all fields
  return {
    showBaseUrl: true,
    showDeploymentName: true,
    showApiVersion: true,
    showOrganizationId: true,
    showProjectId: true,
    showSecretKey: false,
    baseUrlLabel: 'Base URL',
    baseUrlPlaceholder: 'https://api.example.com',
    apiKeyLabel: 'API Key',
    deploymentNamePlaceholder: '',
    apiVersionPlaceholder: '',
  };
};

export const ProviderConfigForm = ({
  open,
  onClose,
  onSaved,
  config,
  providers,
  configuredProviderIds,
}: ProviderConfigFormProps) => {
  const isEdit = !!config;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Form fields
  const [providerId, setProviderId] = useState('');
  const [configName, setConfigName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [deploymentName, setDeploymentName] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      if (config) {
        setProviderId(config.providerId);
        setConfigName(config.name || '');
        setApiKey(''); // Never pre-fill API key
        setSecretKey(''); // Never pre-fill secret key
        setBaseUrl(config.baseUrl || '');
        setOrganizationId(config.organizationId || '');
        setProjectId(config.projectId || '');
        setDeploymentName(config.deploymentName || '');
        setApiVersion(config.apiVersion || '');
        setIsActive(config.isActive);
      } else {
        setProviderId('');
        setConfigName('');
        setApiKey('');
        setSecretKey('');
        setBaseUrl('');
        setOrganizationId('');
        setProjectId('');
        setDeploymentName('');
        setApiVersion('');
        setIsActive(true);
      }
      setError(null);
      setShowApiKey(false);
    }
  }, [open, config]);

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId),
    [providers, providerId]
  );

  const providerFields = useMemo(
    () => getProviderFields(selectedProvider?.displayName || ''),
    [selectedProvider]
  );

  const handleSubmit = async () => {
    setError(null);

    if (!providerId) {
      setError('Please select a provider.');
      return;
    }

    // For Amazon Bedrock, build metadataJson from secret key when provided
    const metadataJson =
      providerFields.showSecretKey && secretKey
        ? JSON.stringify({ secretAccessKey: secretKey })
        : undefined;

    setSaving(true);
    try {
      if (isEdit && config) {
        await llmService.updateProviderConfig(config.id, {
          name: configName.trim() || undefined,
          apiKey: apiKey || undefined, // undefined means no change
          baseUrl: baseUrl || undefined,
          organizationId: organizationId || undefined,
          projectId: projectId || undefined,
          deploymentName: deploymentName || undefined,
          apiVersion: apiVersion || undefined,
          isActive,
          metadataJson: providerFields.showSecretKey ? (secretKey ? metadataJson : undefined) : undefined,
        });
        addNotification('Provider configuration updated successfully', 'success');
      } else {
        await llmService.createProviderConfig({
          providerId,
          name: configName.trim() || undefined,
          apiKey: apiKey || undefined,
          baseUrl: baseUrl || undefined,
          organizationId: organizationId || undefined,
          projectId: projectId || undefined,
          deploymentName: deploymentName || undefined,
          apiVersion: apiVersion || undefined,
          metadataJson,
        });
        addNotification('Provider configuration created successfully', 'success');
      }
      onSaved();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save provider configuration');
    } finally {
      setSaving(false);
    }
  };

  const { addNotification } = useNotificationStore();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? `Edit ${config?.providerName} Configuration` : 'Add Provider Configuration'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Provider Selection */}
          <FormControl fullWidth disabled={isEdit}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={providerId}
              label="Provider"
              onChange={(e) => setProviderId(e.target.value)}
            >
              {providers.map((p) => {
                const alreadyConfigured =
                  !isEdit && (configuredProviderIds?.has(p.id) ?? false);
                return (
                  <MenuItem key={p.id} value={p.id} disabled={alreadyConfigured}>
                    {p.displayName}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({p.modelCount} models)
                    </Typography>
                    {alreadyConfigured && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1, fontStyle: 'italic' }}
                      >
                        {'\u2014 already configured (edit existing)'}
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {/* Dynamic Fields Based on Provider */}
          {providerId && (
            <>
              {/* Friendly Name */}
              <TextField
                label="Name"
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                fullWidth
                placeholder="e.g. Production OpenAI"
                helperText="Optional friendly name to identify this configuration"
              />

              {/* API Key / Access Key ID */}
              <TextField
                label={providerFields.apiKeyLabel}
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                fullWidth
                placeholder={
                  providerFields.showSecretKey
                    ? 'e.g. AKIA...'
                    : isEdit && config?.hasApiKey
                      ? '(unchanged - enter new key to update)'
                      : 'Enter API key'
                }
                helperText={
                  providerFields.showSecretKey
                    ? 'Optional when using IAM role or environment credentials'
                    : isEdit && config?.hasApiKey
                      ? config?.apiKeyLast5
                        ? `Current key ends with \u2026${config.apiKeyLast5}. Leave empty to keep it.`
                        : 'Leave empty to keep the existing key'
                      : 'Your API key will be stored securely'
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowApiKey(!showApiKey)}
                        edge="end"
                        size="small"
                      >
                        {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Secret Access Key (Amazon Bedrock only) */}
              {providerFields.showSecretKey && (
                <TextField
                  label="Secret Access Key"
                  type={showApiKey ? 'text' : 'password'}
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  fullWidth
                  placeholder={isEdit ? '(unchanged - enter new secret to update)' : 'Optional when using IAM or env credentials'}
                  helperText="Leave empty if using IAM role or AWS environment variables"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowApiKey(!showApiKey)}
                          edge="end"
                          size="small"
                        >
                          {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Base URL / Region */}
              {providerFields.showBaseUrl && (
                <TextField
                  label={providerFields.baseUrlLabel}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  fullWidth
                  placeholder={providerFields.baseUrlPlaceholder}
                />
              )}

              {/* Organization ID (OpenAI) */}
              {providerFields.showOrganizationId && (
                <TextField
                  label="Organization ID"
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                  fullWidth
                  placeholder="org-xxxxxxxx"
                  helperText="Optional - Your OpenAI organization ID"
                />
              )}

              {/* Project ID (Azure OpenAI) */}
              {providerFields.showProjectId && (
                <TextField
                  label="Project ID"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  fullWidth
                  placeholder={selectedProvider?.displayName.includes('Azure') ? 'Azure resource group or project' : 'Project ID'}
                  helperText="Optional"
                />
              )}

              {/* Deployment Name (Azure OpenAI) */}
              {providerFields.showDeploymentName && (
                <TextField
                  label="Deployment Name"
                  value={deploymentName}
                  onChange={(e) => setDeploymentName(e.target.value)}
                  fullWidth
                  placeholder={providerFields.deploymentNamePlaceholder}
                  helperText="The name of your model deployment"
                />
              )}

              {/* API Version */}
              {providerFields.showApiVersion && (
                <TextField
                  label="API Version"
                  value={apiVersion}
                  onChange={(e) => setApiVersion(e.target.value)}
                  fullWidth
                  placeholder={providerFields.apiVersionPlaceholder}
                  helperText="Optional - Specific API version to use"
                />
              )}

              {/* Active Status (only on edit) */}
              {isEdit && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  }
                  label="Active"
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !providerId}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
